import React from "react";

import { Box, Grid, makeStyles } from "@material-ui/core";
import MaterialTable, { MTableBodyRow as Row } from "material-table";

import PostcodeChip from "@ds-developer/postcode-chip";

import { DistrictsTableProps, RowData } from "./types";

// This is necessary because the width prop doesn't exist in the typings, but is the recommended way to have fixed column widths 🤷‍♂️
declare module "material-table" {
  // eslint-disable-next-line
  export interface Column<RowData extends object> {
    width?: string;
  }
}

const useStyles = makeStyles(theme => ({
  row: {
    [theme.breakpoints.up("md")]: {
      height: "72px",
    },
  },
}));

const DistrictsTable: React.FC<DistrictsTableProps> = ({
  data = [],
  onSelectionChange = () => {},
  onChipCheck = () => {},
  readOnly = false,
}) => {
  const classes = useStyles();
  const rowDataWithTableData = data.map(rowData => {
    const allAreasSelected =
      rowData.sectors.length > 0 && rowData.sectors.every(s => s.isSelected);

    return {
      ...rowData,
      tableData: { checked: allAreasSelected, size: "small" },
    };
  });

  return (
    <Box className="mobile-friendly">
      <MaterialTable
        style={{ boxShadow: "none" }}
        columns={[
          {
            title: "Postcode",
            field: "postcode",
            sorting: false,
            searchable: false,
            width: "100px",
            editable: readOnly ? "never" : "always",
            render: ({ postcode }) => (
              <div data-label="Postcode">{postcode}</div>
            ),
          },
          {
            title: "Sectors of districts (tap to turn on or off)",
            sorting: false,
            searchable: false,
            width: "400px",
            render: rowData => (
              <div data-label="Sectors">
                <Grid container spacing={3}>
                  {rowData.sectors.some(s => s.isSelected) &&
                    rowData.sectors.map(s => (
                      <Grid
                        item
                        data-test={`district-chip-${rowData.id}`}
                        key={s.id}
                      >
                        <PostcodeChip
                          postcode={s.name}
                          isSelected={s.isSelected}
                          onClick={() => onChipCheck(s.id, rowData)}
                          disabled={readOnly}
                        />
                      </Grid>
                    ))}
                </Grid>
              </div>
            ),
            editable: readOnly ? "never" : "always",
          },
        ]}
        data={rowDataWithTableData}
        localization={{ header: { actions: "" } }}
        onSelectionChange={onSelectionChange}
        options={{
          draggable: false,
          paging: false,
          search: false,
          selection: !readOnly,
          selectionProps: (rowData: RowData) => {
            const someAreasSelected =
              rowData.sectors.length > 0 &&
              rowData.sectors.some(s => s.isSelected) &&
              !rowData.sectors.every(s => s.isSelected);

            if (someAreasSelected) {
              return { indeterminate: true, size: "small" };
            }

            return { size: "small" };
          },
          toolbar: false,
          tableLayout: "fixed",
          showSelectAllCheckbox: true,
          showTextRowsSelected: false,
        }}
        components={{
          Row: props => (
            <Row
              className={classes.row}
              data-test={`districts-table-row-${props.index}`}
              {...props}
            />
          ),
        }}
      />
    </Box>
  );
};

export default DistrictsTable;
